// Desktop navigation only. Minimog's Megamenu still owns the mobile drawer
// and promotional product sliders.
class SlimlineDesktopMenu {
  constructor(container) {
    this.container = container;
    this.items = [...container.querySelectorAll(".m-menu > .m-menu__item")];
    this.activeItem = null;
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.events = new AbortController();
    this.attachEvents();
  }

  attachEvents() {
    const options = { signal: this.events.signal };
    this.container.querySelectorAll(".m-header__menu").forEach((nav) => {
      nav.dataset.menuEnhanced = "true";
    });
    this.items.forEach((item) => {
      const panel = item.querySelector(".m-mega-menu");
      if (panel) {
        panel.setAttribute("aria-hidden", "true");
        panel.inert = true;
      }
      item.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "touch" || !window.matchMedia("(hover: hover)").matches) return;
        clearTimeout(this.timeoutEnter);
        clearTimeout(this.timeoutLeave);
        this.timeoutEnter = setTimeout(() => {
          panel ? this.openItem(item) : this.closeMenu();
        }, 100);
      }, options);
      item.addEventListener("pointerleave", (event) => {
        if (event.pointerType === "touch") return;
        clearTimeout(this.timeoutEnter);
        this.timeoutLeave = setTimeout(() => {
          if (!item.contains(document.activeElement) && this.activeItem === item) this.closeMenu();
        }, 140);
      }, options);
      item.addEventListener("focusin", (event) => {
        // Disclosures open on activation, not focus. Otherwise a pointer click
        // immediately undoes the open caused by its own focusin event.
        if (event.target.closest(".m-menu__toggle")) return;
        panel ? this.openItem(item) : this.closeMenu();
      }, options);
      item.addEventListener("focusout", (event) => {
        if (!item.contains(event.relatedTarget) && this.activeItem === item) this.closeMenu();
      }, options);
      item.querySelector(".m-menu__toggle")?.addEventListener("click", () => {
        this.activeItem === item ? this.closeMenu() : this.openItem(item);
      }, options);
      item.addEventListener("keydown", (event) => {
        if (!panel || !["ArrowDown", "ArrowUp"].includes(event.key) || panel.contains(event.target)) return;
        event.preventDefault();
        this.openItem(item);
        const links = panel.querySelectorAll('a[href], button:not([disabled])');
        const target = event.key === "ArrowUp" ? links[links.length - 1] : links[0];
        target?.focus();
      }, options);
      panel?.addEventListener("click", (event) => {
        const indexLink = event.target.closest(".slimline-make-index__link");
        if (indexLink) {
          event.preventDefault();
          this.jumpToMake(panel, indexLink);
          return;
        }
        if (event.target.closest("a[href]")) this.closeMenu();
      }, options);
    });
    document.addEventListener("pointerdown", (event) => {
      if (!this.container.contains(event.target) || event.target.closest(".m-header__overlay")) this.closeMenu();
    }, options);
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.activeItem) {
        event.preventDefault();
        this.closeMenu(true);
      }
    }, options);
    window.addEventListener("resize", () => {
      if (window.innerWidth < 1024) this.closeMenu();
      else if (this.activeItem) this.updateSize(this.activeItem);
    }, options);
  }

  openItem(item) {
    if (window.innerWidth < 1024) return;
    const panel = item.querySelector(".m-mega-menu");
    if (!panel) return;
    clearTimeout(this.timeoutEnter);
    clearTimeout(this.timeoutLeave);
    if (this.activeItem === item) return;
    this.closeMenu();
    this.activeItem = item;
    item.classList.add("m-menu__item--active");
    item.querySelector(".m-menu__toggle")?.setAttribute("aria-expanded", "true");
    panel.setAttribute("aria-hidden", "false");
    panel.inert = false;
    if (panel.classList.contains("m-mega-menu--make-model")) {
      panel.scrollTop = 0;
      panel.querySelectorAll(".slimline-make-index__link").forEach((link) => link.removeAttribute("aria-current"));
    }
    if (item.classList.contains("m-menu__item--mega")) {
      this.container.classList.add("m-mega-active", "slide-reveal");
    }
    const swiper = panel.querySelector(".swiper-container")?.swiper;
    // Use the actual instance where possible; Minimog keeps a legacy index map.
    const slider = swiper || window.MinimogTheme?.headerSliders?.[item.dataset.index];
    slider?.update();
    this.updateSize(item);
  }

  closeMenu(returnFocus = false) {
    clearTimeout(this.timeoutEnter);
    clearTimeout(this.timeoutLeave);
    const item = this.activeItem;
    this.activeItem = null;
    if (item) {
      item.classList.remove("m-menu__item--active");
      const toggle = item.querySelector(".m-menu__toggle");
      toggle?.setAttribute("aria-expanded", "false");
      const panel = item.querySelector(".m-mega-menu");
      panel?.setAttribute("aria-hidden", "true");
      if (panel) panel.inert = true;
      if (returnFocus) toggle?.focus();
    }
    this.container.classList.remove("m-mega-active", "slide-from-right", "slide-reveal");
  }

  updateSize(item) {
    const panel = item.querySelector(".m-mega-menu");
    if (panel?.classList.contains("m-mega-menu--make-model")) {
      const availableHeight = Math.max(120, window.innerHeight - panel.getBoundingClientRect().top - 16);
      panel.style.setProperty("--slimline-panel-max-height", `${availableHeight}px`);
    }
    const rect = panel?.getBoundingClientRect();
    if (!rect) return;
    // Native Minimog CSS multiplies these unitless values by 1px.
    this.container.style.setProperty("--sf-dropdown-width", rect.width);
    this.container.style.setProperty("--sf-dropdown-height", rect.height);
  }

  jumpToMake(panel, indexLink) {
    const target = document.getElementById(indexLink.hash.slice(1));
    if (!target || !panel.contains(target)) return;
    const index = panel.querySelector(".slimline-make-index");
    const top = panel.scrollTop + target.getBoundingClientRect().top
      - panel.getBoundingClientRect().top - (index?.offsetHeight || 0) - 12;
    panel.scrollTo({ top: Math.max(0, top), behavior: "instant" });
    // Move keyboard focus without also scrolling the underlying page.
    requestAnimationFrame(() => {
      if (this.activeItem?.contains(panel)) target.querySelector("a[href]")?.focus({ preventScroll: true });
    });
    panel.querySelectorAll(".slimline-make-index__link").forEach((link) => {
      if (link === indexLink) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    });
  }

  destroy() {
    this.closeMenu();
    this.events.abort();
    this.container.querySelectorAll(".m-header__menu").forEach((nav) => {
      delete nav.dataset.menuEnhanced;
    });
    this.items.forEach((item) => {
      const panel = item.querySelector(".m-mega-menu");
      panel?.removeAttribute("aria-hidden");
      if (panel) panel.inert = false;
    });
  }
}